import React from 'react';
import { Skeleton } from './ui/skeleton';
import { Card, CardContent, CardHeader } from './ui/card';

/**
 * Skeleton loader for TeamList component
 */
export const TeamListSkeleton: React.FC = () => {
    return (
        <Card className="border-none shadow-lg bg-gray-50/50">
            <CardHeader>
                <div className="flex items-center justify-between">
                    <Skeleton className="h-8 w-32" />
                    <Skeleton className="h-9 w-24" />
                </div>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-1 gap-4">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="rounded-lg border bg-white p-6 space-y-3">
                            <Skeleton className="h-6 w-3/4" />
                            <Skeleton className="h-4 w-full" />
                            <Skeleton className="h-3 w-32" />
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
};

/**
 * Skeleton loader for FeedbackBoard component
 */
export const FeedbackBoardSkeleton: React.FC = () => {
    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {[1, 2, 3].map((i) => (
                <Card key={i}>
                    <CardHeader className="border-l-4 border-gray-300">
                        <div className="flex items-start justify-between gap-2">
                            <Skeleton className="h-6 w-32" />
                            <Skeleton className="h-5 w-8 rounded-full" />
                        </div>
                        <Skeleton className="h-4 w-48 mt-2" />
                    </CardHeader>
                    <CardContent className="space-y-3 pt-4">
                        <Skeleton className="h-10 w-full" />
                        {[1, 2, 3].map((j) => (
                            <div key={j} className="rounded-lg border p-3 space-y-2">
                                <Skeleton className="h-4 w-full" />
                                <Skeleton className="h-4 w-3/4" />
                                <div className="flex items-center justify-between pt-2">
                                    <Skeleton className="h-4 w-20" />
                                    <Skeleton className="h-8 w-16" />
                                </div>
                            </div>
                        ))}
                    </CardContent>
                </Card>
            ))}
        </div>
    );
};

/**
 * Skeleton loader for ActionItemList component
 */
export const ActionItemListSkeleton: React.FC = () => {
    return (
        <div className="space-y-6">
            {/* Filters Skeleton */}
            <Card>
                <CardHeader>
                    <Skeleton className="h-6 w-24" />
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="space-y-2">
                                <Skeleton className="h-4 w-20" />
                                <Skeleton className="h-10 w-full" />
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {/* Stats Skeleton */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[1, 2, 3, 4].map((i) => (
                    <Card key={i}>
                        <CardContent className="pt-6 text-center space-y-2">
                            <Skeleton className="h-9 w-16 mx-auto" />
                            <Skeleton className="h-4 w-20 mx-auto" />
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Action Items Grid Skeleton */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                    <Card key={i}>
                        <CardContent className="pt-6 space-y-3">
                            <div className="flex items-start justify-between">
                                <Skeleton className="h-6 w-3/4" />
                                <Skeleton className="h-6 w-20" />
                            </div>
                            <Skeleton className="h-4 w-full" />
                            <Skeleton className="h-4 w-2/3" />
                            <div className="flex items-center justify-between pt-2">
                                <Skeleton className="h-4 w-24" />
                                <Skeleton className="h-4 w-24" />
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    );
};

/**
 * Skeleton loader for RecentRetrospectives component
 */
export const RecentRetrospectivesSkeleton: React.FC = () => {
    return (
        <Card className="border-none shadow-lg bg-gradient-to-br from-white to-gray-50">
            <CardHeader>
                <div className="flex items-center justify-between">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-9 w-24" />
                </div>
            </CardHeader>
            <CardContent>
                <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="rounded-lg border bg-white p-4 space-y-3">
                            <div className="flex items-start justify-between">
                                <Skeleton className="h-5 w-2/3" />
                                <Skeleton className="h-5 w-16" />
                            </div>
                            <div className="flex items-center gap-3">
                                <Skeleton className="h-4 w-32" />
                                <Skeleton className="h-5 w-20" />
                            </div>
                            <div className="flex items-center gap-2">
                                <Skeleton className="h-3 w-24" />
                                <Skeleton className="h-3 w-20" />
                            </div>
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
};

/**
 * Skeleton loader for ActionItemSummary component
 */
export const ActionItemSummarySkeleton: React.FC = () => {
    return (
        <Card className="border-none shadow-lg bg-gradient-to-br from-white to-gray-50">
            <CardHeader>
                <div className="flex items-center justify-between">
                    <Skeleton className="h-8 w-32" />
                    <Skeleton className="h-9 w-24" />
                </div>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-2 gap-4 mb-6">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="text-center space-y-2">
                            <Skeleton className="h-8 w-16 mx-auto" />
                            <Skeleton className="h-4 w-20 mx-auto" />
                        </div>
                    ))}
                </div>
                <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="rounded-lg border bg-white p-3 space-y-2">
                            <div className="flex items-start justify-between">
                                <Skeleton className="h-5 w-2/3" />
                                <Skeleton className="h-5 w-16" />
                            </div>
                            <Skeleton className="h-4 w-24" />
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
};

/**
 * Skeleton loader for MetricCard component
 */
export const MetricCardSkeleton: React.FC = () => {
    return (
        <Card>
            <CardContent className="pt-6 space-y-3">
                <div className="flex items-start justify-between">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-5 w-16" />
                </div>
                <Skeleton className="h-10 w-32" />
                <Skeleton className="h-16 w-full" />
            </CardContent>
        </Card>
    );
};

/**
 * Skeleton loader for TrendChart component
 */
export const TrendChartSkeleton: React.FC = () => {
    return (
        <Card>
            <CardHeader>
                <Skeleton className="h-6 w-48" />
            </CardHeader>
            <CardContent>
                <Skeleton className="h-64 w-full" />
            </CardContent>
        </Card>
    );
};

/**
 * Skeleton loader for Analytics Dashboard
 */
export const AnalyticsDashboardSkeleton: React.FC = () => {
    return (
        <div className="space-y-6">
            {/* Metric Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[1, 2, 3, 4].map((i) => (
                    <MetricCardSkeleton key={i} />
                ))}
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <TrendChartSkeleton />
                <TrendChartSkeleton />
            </div>
        </div>
    );
};

/**
 * Generic page skeleton loader
 */
export const PageSkeleton: React.FC = () => {
    return (
        <div className="space-y-6">
            <div className="space-y-2">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-5 w-96" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                    <Card key={i}>
                        <CardHeader>
                            <Skeleton className="h-6 w-32" />
                            <Skeleton className="h-4 w-48 mt-2" />
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <Skeleton className="h-24 w-full" />
                            <Skeleton className="h-24 w-full" />
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    );
};
